import React from 'react';
import { StyleSheet, View } from 'react-native';
import {
  SkeletonBone,
  SkeletonCard,
  SkeletonGroup,
  useSkeletonBoneColor,
} from '@/components/common/skeleton';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { RADIUS } from '@/constants/shared/theme';
import { useTypography } from '@/hooks/shared/useTypography';

const ROW_COUNT = 4;
/**
 * Pill widths of the [In progress] [Done] [Skipped] status cluster — with the
 * 6pt gaps the cluster is ~214pt wide, the same as the real one, so the row
 * wraps the pills under the label at the same widths (phones) and keeps them
 * inline at the same widths (desktop).
 */
const PILL_WIDTHS = [84, 50, 68] as const;

interface Props {
  /**
   * Which header to prefigure: the mobile view's 20pt title row, or the
   * desktop view's DesktopPageHeader (heading1 title, body subtitle, page
   * paddings). The checklist card below is identical on both.
   */
  variant: 'mobile' | 'desktop';
}

/**
 * Loading placeholder for the occurrence detail, rendered by both occurrence
 * views under their live back link in the slot the header + checklist card take
 * once the list lands. Mirrors the real geometry so nothing jumps: the header
 * (title + filled Edit action, then the cadence · period subtitle) and a
 * GlassCard (24 horizontal / 4 vertical padding, 1pt border) of
 * `OccurrenceItemRow`s — label + meta line on the left, the three status pills
 * on the right, hairline dividers between rows, 56pt per row. No outer padding;
 * both hosts pad their scroll content.
 */
export const TodoListOccurrenceSkeleton: React.FC<Props> = ({ variant }) => {
  const dividerColor = useSkeletonBoneColor();
  const type = useTypography();

  return (
    <SkeletonGroup style={styles.container}>
      {variant === 'desktop' ? (
        <View style={styles.desktopHeader}>
          <View style={styles.desktopTitleRow}>
            <SkeletonBone width="40%" height={type.heading1.fontSize} />
            <SkeletonBone width={96} height={37} borderRadius={RADIUS.sm} />
          </View>
          <View style={styles.desktopSubtitleRow}>
            <SkeletonBone width={140} height={type.body.fontSize} />
          </View>
        </View>
      ) : (
        <View style={styles.mobileHeader}>
          <View style={styles.mobileTitleRow}>
            <SkeletonBone width="60%" height={20} />
            <SkeletonBone width={78} height={28} borderRadius={RADIUS.sm} />
          </View>
          <View style={styles.mobileSubtitleRow}>
            <SkeletonBone width={120} height={13} />
          </View>
        </View>
      )}

      <SkeletonCard
        padding={REDESIGN_SPACING.cardPadding}
        borderRadius={REDESIGN_SPACING.cardBorderRadius}
        style={styles.card}
      >
        {Array.from({ length: ROW_COUNT }).map((_, i) => (
          <View key={i}>
            {i > 0 && (
              <View style={[styles.divider, { backgroundColor: dividerColor }]} />
            )}
            <View style={styles.row}>
              <View style={styles.labelWrap}>
                <SkeletonBone width="55%" height={15} />
                <SkeletonBone width={120} height={12} />
              </View>
              <View style={styles.pills}>
                {PILL_WIDTHS.map(width => (
                  <SkeletonBone
                    key={width}
                    width={width}
                    height={27}
                    borderRadius={14}
                  />
                ))}
              </View>
            </View>
          </View>
        ))}
      </SkeletonCard>
    </SkeletonGroup>
  );
};

const styles = StyleSheet.create({
  // Matches the views' `scrollContent` gap between header and card.
  container: { gap: 16 },
  // Mobile header: 20pt title beside the 28pt filled Edit button, 6pt gap, 13pt
  // subtitle — the real `header` / `headerTop` / `subtitle` boxes.
  mobileHeader: { gap: 6 },
  mobileTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    minHeight: 28,
  },
  mobileSubtitleRow: { minHeight: 16, justifyContent: 'center' },
  // Desktop header: PageHeader's 24 top / 20 bottom padding + 4 gap, plus
  // DesktopPageHeader's 4pt bottom padding; the title row is as tall as the
  // 37pt filled Edit action.
  desktopHeader: { paddingTop: 24, paddingBottom: 24, gap: 4 },
  desktopTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 37,
  },
  // The 14pt body subtitle's default line box.
  desktopSubtitleRow: { minHeight: 17, justifyContent: 'center' },
  // GlassCard draws a 1pt border and the views override its vertical padding to
  // 4; rows stack with dividers, so no gap between them.
  card: {
    gap: 0,
    paddingVertical: 4,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  divider: { height: StyleSheet.hairlineWidth },
  // `OccurrenceItemRow`: 10pt vertical padding around a 15pt label + 16pt meta
  // line (36pt block) and the 27pt status pills. The row wraps and the label
  // block keeps its 160pt minimum exactly like the real row, so the pills drop
  // under the label at the same widths.
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    paddingVertical: 10,
    flexWrap: 'wrap',
  },
  labelWrap: { flex: 1, gap: 2, minHeight: 36, minWidth: 160 },
  pills: { flexDirection: 'row', alignItems: 'center', gap: 6 },
});
